import React, { useEffect, useState, useRef, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { CheckCircle, AlertCircle } from 'lucide-react';
import ClinicianAdminEdit from '../components/clinician/ClinicianAdminEdit';

import { DashboardLayout } from '../layouts/DashboardLayout';
import useFetchDetails from '../hooks/Clinic/useFetchClinicianDetails';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../redux';
import { updateClinician } from '../redux/clinic/clinicSlice';

// Matches the backend schema: clinician.location[{ id_location, name, postal_code }]
export interface LocationEntry {
  id_location?: string;
  name?: string;
  postal_code?: string;
}

// Type for the clinician data expected by ClinicianAdminEdit
interface ClinicianData {
  _id: string;
  clinicianId: string;
  firstName: string;
  lastName: string;
  fullName: string;
  email: string;
  emailSource: string;
  npiNumber: string;
  titles: string[];
  role: string;
  status: string;
  phone: string;
  specialties: string[];
  licenses: string[];
  licenseDetails: { number: string; type: string; state: string }[];
  slug: string;
  isActive: boolean;
  id_practice: string;
  createdAt: string;
  updatedAt: string;
  gender?: string;
  ethnicity?: string;
  experience?: string;
  education?: string[];
  address?: {
    locations?: LocationEntry[];
  };
  profile: {
    detailsScrapedAt: string;
    detailsSource: string;
    editUrl: string;
    bio?: string;
    profileImage?: string;
    vslVideo?: string;
    rates?: Array<{ sessionType: string; amount: number; currency: string }>;
    sessionTypes?: Array<{ type: string; duration: number; rate?: number; sessionTarget?: string }>;
    maxSessions?: { perWeek?: number };
    insuranceAccepted?: string[];
    languages?: string[];
    modalities?: string[];
    ageRanges?: string[];
    sessionLength?: string;
    sessionPrice?: string;
  };
  availability?: {
    weeklySchedule?: {
      monday?: Array<{ start: string; end: string; available: boolean }>;
      tuesday?: Array<{ start: string; end: string; available: boolean }>;
      wednesday?: Array<{ start: string; end: string; available: boolean }>;
      thursday?: Array<{ start: string; end: string; available: boolean }>;
      friday?: Array<{ start: string; end: string; available: boolean }>;
      saturday?: Array<{ start: string; end: string; available: boolean }>;
      sunday?: Array<{ start: string; end: string; available: boolean }>;
    };
    /** Calendar-scraped slots (read-only in edit UI) */
    open?: Array<{ time?: string; eventText?: string; date?: string; unixTime?: number }>;
    booked?: Array<{ time?: string; eventText?: string; date?: string; unixTime?: number }>;
    cancelled?: Array<{ time?: string; eventText?: string; date?: string; unixTime?: number }>;
    outOfOffice?: Array<{ time?: string; eventText?: string; date?: string; unixTime?: number }>;
    blocked?: Array<{ time?: string; eventText?: string; date?: string; unixTime?: number }>;
  };
  availabilityUpdatedAt?: string;
  lastCalendarScrape?: string;
}

export const ClinicianManagementPage = () => {
  const { clinicianId } = useParams<{ clinicianId: string }>();
  const navigate = useNavigate();
  const { selectedClinician, isLoading, error, fetchClinicianData } = useFetchDetails();
  const { selectedPractice } = useSelector((state: RootState) => state.practices);
  const dispatch = useDispatch<AppDispatch>();
  const [isSaving, setIsSaving] = useState(false);
  const [notification, setNotification] = useState<{ type: 'success' | 'error'; message: string } | null>(null);
  const contentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (clinicianId) {
      fetchClinicianData(clinicianId);
    }
  }, [clinicianId, fetchClinicianData]);

  // Convert Clinician to ClinicianData format - SAME AS ADMIN PAGE
  // IMPORTANT: Most fields are at ROOT level, NOT in profile object
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const convertToClinicianData = (clinician: any): ClinicianData => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const profile: any = clinician.profile || {};
    
    // Handle availability - API returns calendar-based structure, but we need weeklySchedule for editing
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    let availability: any = clinician.availability || {};

    if (!availability.weeklySchedule) {
      // Try to build weeklySchedule from per-day arrays directly on availability, if present
      const days = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'] as const;
      const weeklyScheduleFromRoot: Record<string, unknown[]> = {};

      days.forEach((day) => {
        const value = availability[day];
        if (Array.isArray(value) && value.length > 0) {
          weeklyScheduleFromRoot[day] = value;
        }
      });

      if (Object.keys(weeklyScheduleFromRoot).length > 0) {
        availability = {
          ...availability,
          weeklySchedule: weeklyScheduleFromRoot,
        };
      } else {
        availability = {
          ...availability, // Preserve calendar data (open, booked, etc.)
          weeklySchedule: {
            monday: [],
            tuesday: [],
            wednesday: [],
            thursday: [],
            friday: [],
            saturday: [],
            sunday: [],
          },
        };
      }
    }
    
    // Handle rates - can be object {} or array
    let ratesArray: Array<{ sessionType: string; amount: number; currency: string }> = [];
    if (Array.isArray(clinician.rates)) {
      ratesArray = clinician.rates.map((r: unknown) => {
        const rate = r as { type?: string; sessionType?: string; amount?: string | number; currency?: string };
        return {
          sessionType: rate.type || rate.sessionType || 'Individual',
          amount: typeof rate.amount === 'string' ? parseFloat(rate.amount.replace(/[^0-9.]/g, '')) : (rate.amount || 0),
          currency: rate.currency || 'USD'
        };
      });
    } else if (clinician.rates && typeof clinician.rates === 'object') {
      // Convert rates object to array format
      const ratesObj = clinician.rates as Record<string, unknown>;
      Object.keys(ratesObj).forEach(key => {
        const amount = ratesObj[key];
        if (amount && (typeof amount === 'number' || typeof amount === 'string')) {
          ratesArray.push({
            sessionType: key === 'perIndividual' || key === 'perSession' ? 'Individual' : 
                        key === 'perCouple' ? 'Couples' :
                        key === 'perGroup' ? 'Group' :
                        key === 'perFamily' ? 'Family' : key,
            amount: typeof amount === 'string' ? parseFloat(amount.replace(/[^0-9.]/g, '')) : amount,
            currency: 'USD'
          });
        }
      });
    }
    
    return {
      _id: String(clinician._id || clinician.id || ''),
      clinicianId: String(clinician.clinicianId || clinician._id || clinician.id || ''),
      firstName: String(clinician.firstName || ''),
      lastName: String(clinician.lastName || ''),
      fullName: String(clinician.fullName || `${clinician.firstName || ''} ${clinician.lastName || ''}`.trim()),
      email: String(clinician.email || ''),
      emailSource: String(clinician.emailSource || ''),
      npiNumber: String(clinician.npiNumber || ''),
      titles: Array.isArray(clinician.titles)
        ? clinician.titles.map(String)
        : clinician.title ? [String(clinician.title)] : [],
      role: String(clinician.role || ''),
      status: String(clinician.status || 'pending'),
      phone: String(clinician.phone || clinician.phone_number || ''),
      specialties: Array.isArray(clinician.specialties)
        ? clinician.specialties.map((s: unknown) => typeof s === 'string' ? s : (s as { name?: string })?.name || String(s))
        : [],
      licenses: Array.isArray(clinician.licenses) ? clinician.licenses.map(String) : [],
      licenseDetails: (() => {
        // Backend schema: licenses: [{ number, type, state }]
        if (Array.isArray(clinician.licenses) && clinician.licenses.length > 0 && typeof clinician.licenses[0] === 'object') {
          return (clinician.licenses as Record<string, unknown>[]).map(l => ({
            number: String(l.number || ''),
            type: String(l.type || ''),
            state: String(l.state || ''),
          }));
        }
        // Legacy fallback: state_licenses: [{ license_number, license_type }]
        if (Array.isArray(clinician.state_licenses)) {
          return (clinician.state_licenses as Record<string, unknown>[]).map(l => ({
            number: String(l.license_number || ''),
            type: String(l.license_type || ''),
            state: String(l.state || ''),
          }));
        }
        return [{ number: '', type: '', state: '' }];
      })(),
      slug: String(clinician.slug || ''),
      isActive: Boolean(clinician.isActive),
      id_practice: String(clinician.id_practice || ''),
      createdAt: String(clinician.createdAt || ''),
      updatedAt: String(clinician.updatedAt || ''),
      gender: clinician.gender ? String(clinician.gender) : undefined,
      ethnicity: clinician.ethnicity ? String(clinician.ethnicity) : undefined,
      experience: clinician.experience ? String(clinician.experience) : undefined,
      education: Array.isArray(clinician.education) ? (clinician.education as unknown[]).map(String) : [],
      // Map clinician.location[] (backend schema) → address.locations[]
      address: (() => {
        const rawLocs = Array.isArray(clinician.location) ? clinician.location : [];
        const locations: LocationEntry[] = rawLocs.map((loc: Record<string, unknown>) => ({
          id_location: loc.id_location != null ? String(loc.id_location) : '',
          name: loc.name != null ? String(loc.name) : '',
          postal_code: loc.postal_code != null ? String(loc.postal_code) : '',
        }));
        return { locations: locations.length > 0 ? locations : [{ id_location: '', name: '', postal_code: '' }] };
      })(),
      profile: {
        detailsScrapedAt: String(profile.detailsScrapedAt || ''),
        detailsSource: String(profile.detailsSource || ''),
        editUrl: String(profile.editUrl || ''),
        // bio - check root level first, then profile (same as verification page)
        bio: clinician.bio || profile.bio ? String(clinician.bio || profile.bio) : '',
        profileImage: profile.profileImage || clinician.profileImage ? String(profile.profileImage || clinician.profileImage) : undefined,
        vslVideo: profile.vslVideo || profile.vsl_url || clinician.vslVideo || clinician.vsl_url ? String(profile.vslVideo || profile.vsl_url || clinician.vslVideo || clinician.vsl_url) : undefined,
        // rates - handle both root level object/array
        rates: ratesArray,
        // sessionTypes - check root level first, then profile (same as verification page)
        // Transform to ensure format: { type, duration, rate }
        sessionTypes: (() => {
          const rawSessionTypes = Array.isArray(clinician.sessionTypes) && clinician.sessionTypes.length > 0
            ? clinician.sessionTypes
            : Array.isArray(profile.sessionTypes) && profile.sessionTypes.length > 0 
              ? profile.sessionTypes 
              : clinician.sessionTypes || [];
          
          // Transform to expected format
          return rawSessionTypes.map((st: any) => {
            // If it's already an object with type, duration, rate
            if (typeof st === 'object' && st !== null) {
              return {
                type: st.type || st.sessionType || st.name || String(st),
                duration: st.duration || 50,
                rate: st.rate || st.amount || undefined,
                sessionTarget: st.sessionTarget || undefined
              };
            }
            // If it's a string, convert to object format
            return {
              type: String(st),
              duration: 50,
              rate: undefined,
              sessionTarget: undefined
            };
          });
        })(),
        maxSessions: clinician.maxSessions || profile.maxSessions || { perWeek: undefined },
        // insuranceAccepted - check root level first, then profile (same as verification page)
        insuranceAccepted: Array.isArray(clinician.insuranceAccepted) || Array.isArray(profile.insuranceAccepted)
          ? (clinician.insuranceAccepted || profile.insuranceAccepted || []).map(String)
          : [],
        // languages - check root level first, then profile (same as verification page)
        languages: Array.isArray(clinician.languages) || Array.isArray(profile.languages)
          ? (clinician.languages || profile.languages || ['English']).map(String)
          : ['English'],
        // modality - check root level first, then profile.treatmentModalities (same as verification page)
        modalities: Array.isArray(clinician.modality) || Array.isArray(profile.treatmentModalities)
          ? (clinician.modality || profile.treatmentModalities || []).map(String)
          : [],
        // ageRange - check root level first, then profile.preferredAges (same as verification page)
        ageRanges: Array.isArray(clinician.ageRange) || Array.isArray(profile.preferredAges)
          ? (clinician.ageRange || profile.preferredAges || []).map(String)
          : [],
        sessionLength: profile.sessionLength || clinician.sessionLength ? String(profile.sessionLength || clinician.sessionLength) : undefined,
        sessionPrice: profile.sessionPrice || clinician.sessionPrice ? String(profile.sessionPrice || clinician.sessionPrice) : undefined
      },
      availability: availability,
      availabilityUpdatedAt: clinician.availabilityUpdatedAt ? String(clinician.availabilityUpdatedAt) : undefined,
      lastCalendarScrape: clinician.lastCalendarScrape ? String(clinician.lastCalendarScrape) : undefined
    };
  };

  const handleSave = async (updatedClinician: ClinicianData) => {
    if (!clinicianId) return;
    setIsSaving(true);
    try {
      // Format the data according to backend requirements - SAME AS ADMIN PAGE
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const updateData: any = {
        // Personal Information - Always include
        firstName: updatedClinician.firstName || '',
        lastName: updatedClinician.lastName || '',
        email: updatedClinician.email || '',
        emailSource: updatedClinician.emailSource || '',
        phone: updatedClinician.phone || '',
        phone_number: updatedClinician.phone || '',
        
        // Professional Information
        npiNumber: updatedClinician.npiNumber || '',
        titles: updatedClinician.titles || [],
        role: updatedClinician.role || '',
        bio: updatedClinician.profile?.bio || '',
        profileImage: updatedClinician.profile?.profileImage || '',
        vsl_url: updatedClinician.profile?.vslVideo || '',
        vslVideo: updatedClinician.profile?.vslVideo || '',
        gender: updatedClinician.gender || '',
        ethnicity: updatedClinician.ethnicity || '',
        experience: updatedClinician.experience || '',
        
        // Education - ensure it's an array
        education: Array.isArray(updatedClinician.education) && updatedClinician.education.length > 0 
          ? updatedClinician.education 
          : [],
        
        // Professional Arrays - Always send arrays (even if empty)
        specialties: Array.isArray(updatedClinician.specialties) ? updatedClinician.specialties : [],
        // licenses: [{ number, type, state }] — matches backend schema
        licenses: (updatedClinician.licenseDetails || [])
          .filter(l => l.number.trim() || l.type.trim() || l.state.trim())
          .map(l => ({ number: l.number, type: l.type, state: l.state })),
        
        // Profile Arrays - Professional Data
        languages: Array.isArray(updatedClinician.profile?.languages) && updatedClinician.profile.languages.length > 0
          ? updatedClinician.profile.languages
          : [],
        insuranceAccepted: Array.isArray(updatedClinician.profile?.insuranceAccepted) && updatedClinician.profile.insuranceAccepted.length > 0
          ? updatedClinician.profile.insuranceAccepted
          : [],
        modality: Array.isArray(updatedClinician.profile?.modalities) && updatedClinician.profile.modalities.length > 0
          ? updatedClinician.profile.modalities
          : [],
        ageRange: Array.isArray(updatedClinician.profile?.ageRanges) && updatedClinician.profile.ageRanges.length > 0
          ? updatedClinician.profile.ageRanges
          : [],
        sessionTypes: Array.isArray(updatedClinician.profile?.sessionTypes)
          ? updatedClinician.profile.sessionTypes
          : [],
        maxSessions: updatedClinician.profile?.maxSessions ?? {},
        
        // Session Information
        sessionLength: updatedClinician.profile?.sessionLength || '',
        sessionPrice: updatedClinician.profile?.sessionPrice || '',
        
        // Status
        isActive: updatedClinician.isActive !== undefined ? updatedClinician.isActive : true,
        
        // Send location back as the backend schema array: [{ id_location, name, postal_code }]
        location: (updatedClinician.address?.locations || [])
          .filter(loc => loc.name || loc.postal_code)
          .map(loc => ({
            id_location: loc.id_location || '',
            name: loc.name || '',
            postal_code: loc.postal_code || '',
          })),
        
        // zip_code at root level for backward-compat (first location's postal_code)
        zip_code: updatedClinician.address?.locations?.[0]?.postal_code || undefined,
        
        // Rates - transform to match VerificationPage format exactly
        rates: (() => {
          // Merge rates from sessionTypes and rates array
          const ratesFromSessionTypes = Array.isArray(updatedClinician.profile?.sessionTypes) 
            ? updatedClinician.profile.sessionTypes.filter(st => st.rate !== undefined && st.rate !== null && st.rate !== '')
                .map((st: any) => ({
                  sessionType: st.type || '',
                  amount: typeof st.rate === 'number' ? st.rate : (typeof st.rate === 'string' ? parseFloat(String(st.rate).replace(/[^0-9.]/g, '')) || 0 : 0),
                  currency: 'USD'
                }))
            : [];
            
          const existingRates = Array.isArray(updatedClinician.profile?.rates) && updatedClinician.profile.rates.length > 0
            ? updatedClinician.profile.rates.map((r: any) => ({
                sessionType: r.sessionType || r.type || '',
                amount: typeof r.amount === 'number' ? r.amount : (typeof r.amount === 'string' ? parseFloat(r.amount.replace(/[^0-9.]/g, '')) || 0 : 0),
                currency: r.currency || 'USD'
              }))
            : [];
            
          const ratesArray = ratesFromSessionTypes.length > 0 ? ratesFromSessionTypes : existingRates;
          
          // Create rates object format matching VerificationPage
          if (ratesArray.length > 0) {
            return {
              perSession: ratesArray.find((r: any) => r.sessionType === 'Individual' || r.sessionType === 'individual')?.amount || ratesArray[0]?.amount,
              perGroup: ratesArray.find((r: any) => r.sessionType === 'Group' || r.sessionType === 'group')?.amount,
              perFamily: ratesArray.find((r: any) => r.sessionType === 'Family' || r.sessionType === 'family')?.amount,
              perCouple: ratesArray.find((r: any) => r.sessionType === 'Couples' || r.sessionType === 'Couple' || r.sessionType === 'couples' || r.sessionType === 'couple')?.amount,
              perIndividual: ratesArray.find((r: any) => r.sessionType === 'Individual' || r.sessionType === 'individual')?.amount || ratesArray[0]?.amount
            };
          }
          return undefined;
        })(),
        
        // Also send rates array for direct access (matching VerificationPage)
        profileRates: (() => {
          const ratesFromSessionTypes = Array.isArray(updatedClinician.profile?.sessionTypes) 
            ? updatedClinician.profile.sessionTypes.filter(st => st.rate !== undefined && st.rate !== null && st.rate !== '')
                .map((st: any) => ({
                  sessionType: st.type || '',
                  amount: typeof st.rate === 'number' ? st.rate : (typeof st.rate === 'string' ? parseFloat(String(st.rate).replace(/[^0-9.]/g, '')) || 0 : 0),
                  currency: 'USD'
                }))
            : [];
            
          const existingRates = Array.isArray(updatedClinician.profile?.rates) && updatedClinician.profile.rates.length > 0
            ? updatedClinician.profile.rates.map((r: any) => ({
                sessionType: r.sessionType || r.type || '',
                amount: typeof r.amount === 'number' ? r.amount : (typeof r.amount === 'string' ? parseFloat(r.amount.replace(/[^0-9.]/g, '')) || 0 : 0),
                currency: r.currency || 'USD'
              }))
            : [];
            
          const ratesArray = ratesFromSessionTypes.length > 0 ? ratesFromSessionTypes : existingRates;
          return ratesArray.length > 0 ? ratesArray : undefined;
        })(),
        
        // Add full profile object for backends that expect nested properties
        profile: {
          ...(updatedClinician.profile || {}),
          sessionTypes: Array.isArray(updatedClinician.profile?.sessionTypes) ? updatedClinician.profile.sessionTypes : [],
          rates: (() => {
            const stRates = Array.isArray(updatedClinician.profile?.sessionTypes) 
              ? updatedClinician.profile.sessionTypes.filter(st => st.rate !== undefined && st.rate !== null && st.rate !== '')
                  .map((st: any) => ({
                    sessionType: st.type || '',
                    amount: typeof st.rate === 'number' ? st.rate : (typeof st.rate === 'string' ? parseFloat(String(st.rate).replace(/[^0-9.]/g, '')) || 0 : 0),
                    currency: 'USD'
                  }))
              : [];
            return stRates.length > 0 ? stRates : (updatedClinician.profile?.rates || []);
          })()
        },
        
        // Availability - preserve calendar data (open, booked, etc.) and include weeklySchedule
        availability: selectedClinician && (selectedClinician as any).availability ? {
          ...((selectedClinician as any).availability), // Preserve existing calendar data (open, booked, cancelled, outOfOffice, blocked)
          weeklySchedule: updatedClinician.availability?.weeklySchedule || {
            monday: [],
            tuesday: [],
            wednesday: [],
            thursday: [],
            friday: [],
            saturday: [],
            sunday: []
          }
        } : {
          weeklySchedule: updatedClinician.availability?.weeklySchedule || {
            monday: [],
            tuesday: [],
            wednesday: [],
            thursday: [],
            friday: [],
            saturday: [],
            sunday: []
          }
        },
        
        // Skip invitation update flag
        skipInvitationUpdate: true
      };

      // Remove only null/undefined values, but keep empty strings and empty arrays for professional data
      Object.keys(updateData).forEach(key => {
        // Keep skipInvitationUpdate flag
        if (key === 'skipInvitationUpdate') {
          return; // Don't remove this flag
        }
        // Keep arrays even if empty for professional data
        if (Array.isArray(updateData[key])) {
          return; // Don't remove arrays
        }
        // Keep empty strings for required fields
        if (typeof updateData[key] === 'string' && updateData[key] === '') {
          // Only remove empty strings for optional fields
          const optionalFields = ['emailSource', 'ethnicity', 'experience', 'sessionLength', 'sessionPrice'];
          if (optionalFields.includes(key)) {
            delete updateData[key];
          }
          return;
        }
        // Remove undefined and null
        if (updateData[key] === undefined || updateData[key] === null) {
          delete updateData[key];
        }
      });

      await dispatch(updateClinician({ clinicianId, updateData })).unwrap();
      await fetchClinicianData(clinicianId);
      setNotification({ type: 'success', message: 'Clinician updated successfully.' });
      setTimeout(() => setNotification(null), 4000);
      contentRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    } catch (err) {
      console.error('Failed to update clinician:', err);
      const message = err && typeof err === 'object' && 'message' in err ? String((err as { message: string }).message) : 'Update failed. Please try again.';
      setNotification({ type: 'error', message });
      setTimeout(() => setNotification(null), 6000);
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    navigate(`/practice/clinician/details/${clinicianId}`);
  };

  // Memoize so the object reference only changes when selectedClinician actually changes.
  // Without this, convertToClinicianData creates a new object on every parent render,
  // causing the useEffect([clinician]) in ClinicianAdminEdit to reset form edits.
  const clinicianData = useMemo(
    () => (selectedClinician ? convertToClinicianData(selectedClinician) : null),
    [selectedClinician]
  );

  if (!clinicianId) {
    return (
      <DashboardLayout>
        <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50/30 to-indigo-50/50 flex items-center justify-center p-8">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="relative"
          >
            <div className="absolute inset-0 bg-white/60 backdrop-blur-xl border border-white/30 rounded-3xl shadow-2xl shadow-blue-500/10" />
            <div className="relative px-12 py-10 text-center">
              <div className="w-16 h-16 bg-slate-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <AlertCircle className="w-8 h-8 text-slate-400" />
              </div>
              <p className="text-xl font-semibold text-slate-700">Clinician not found</p>
              <p className="text-slate-500 text-sm mt-1">No clinician ID was provided in the URL.</p>
            </div>
          </motion.div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      {/* Toast notification */}
      <AnimatePresence>
        {notification && (
          <motion.div
            initial={{ opacity: 0, y: -20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -20, scale: 0.95 }}
            transition={{ duration: 0.25, ease: 'easeOut' }}
            className="fixed top-4 right-4 z-50 max-w-sm"
          >
            <div className={`relative overflow-hidden rounded-2xl shadow-2xl border ${
              notification.type === 'success'
                ? 'border-emerald-200/60 shadow-emerald-500/20'
                : 'border-red-200/60 shadow-red-500/20'
            }`}>
              <div className="absolute inset-0 bg-white/80 backdrop-blur-xl" />
              <div className="relative flex items-start gap-3 px-5 py-4">
                <div className={`mt-0.5 flex-shrink-0 p-1.5 rounded-xl ${
                  notification.type === 'success' ? 'bg-emerald-100' : 'bg-red-100'
                }`}>
                  {notification.type === 'success' ? (
                    <CheckCircle className="h-4 w-4 text-emerald-600" />
                  ) : (
                    <AlertCircle className="h-4 w-4 text-red-600" />
                  )}
                </div>
                <div>
                  <p className={`font-semibold text-sm ${
                    notification.type === 'success' ? 'text-emerald-800' : 'text-red-800'
                  }`}>
                    {notification.type === 'success' ? 'Saved successfully' : 'Something went wrong'}
                  </p>
                  <p className="text-slate-600 text-xs mt-0.5 leading-relaxed">{notification.message}</p>
                </div>
              </div>
              {/* Progress bar */}
              <motion.div
                initial={{ scaleX: 1 }}
                animate={{ scaleX: 0 }}
                transition={{ duration: notification.type === 'success' ? 4 : 6, ease: 'linear' }}
                style={{ transformOrigin: 'left' }}
                className={`h-0.5 ${notification.type === 'success' ? 'bg-emerald-400' : 'bg-red-400'}`}
              />
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Full-screen loading overlay */}
      <AnimatePresence>
        {isLoading && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-40 flex items-center justify-center bg-white/50 backdrop-blur-sm"
            aria-busy="true"
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              className="relative"
            >
              <div className="absolute inset-0 bg-white/70 backdrop-blur-xl border border-white/40 rounded-3xl shadow-2xl shadow-blue-500/15" />
              <div className="relative flex flex-col items-center gap-4 px-10 py-8">
                <div className="relative">
                  <div className="w-12 h-12 rounded-full border-2 border-slate-200 border-t-blue-500 animate-spin" />
                  <div className="absolute inset-0 w-12 h-12 rounded-full border-2 border-transparent border-b-indigo-400 animate-spin" style={{ animationDirection: 'reverse', animationDuration: '1.5s' }} />
                </div>
                <div className="text-center">
                  <p className="text-sm font-semibold text-slate-700">Loading clinician</p>
                  <p className="text-xs text-slate-400 mt-0.5">Fetching profile data…</p>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Content */}
      <div ref={contentRef} className="relative min-h-[calc(100vh-4rem)] w-full">
        {!isLoading && clinicianData && (
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, ease: 'easeOut' }}
          >
            <ClinicianAdminEdit
              clinician={clinicianData}
              onSave={handleSave}
              onCancel={handleCancel}
              isSaving={isSaving}
              practiceLocations={selectedPractice?.locations || []}
            />
          </motion.div>
        )}

        {/* Error state */}
        {!isLoading && !clinicianData && error && (
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="flex items-center justify-center min-h-[calc(100vh-4rem)] p-8"
          >
            <div className="relative max-w-sm w-full">
              <div className="absolute inset-0 bg-white/60 backdrop-blur-xl border border-red-100/60 rounded-3xl shadow-2xl shadow-red-500/10" />
              <div className="relative px-10 py-10 text-center">
                <div className="w-16 h-16 bg-red-50 rounded-2xl flex items-center justify-center mx-auto mb-5">
                  <AlertCircle className="w-8 h-8 text-red-400" />
                </div>
                <p className="text-lg font-semibold text-slate-800">Failed to load clinician</p>
                <p className="text-slate-500 text-sm mt-2 leading-relaxed">{typeof error === 'string' ? error : 'An unexpected error occurred. Please try again.'}</p>
                <button
                  onClick={() => clinicianId && fetchClinicianData(clinicianId)}
                  className="mt-6 inline-flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-blue-600 to-indigo-600 text-white text-sm font-medium rounded-xl shadow-lg shadow-blue-500/30 hover:from-blue-600/90 hover:to-indigo-600/90 transition-all"
                >
                  Try again
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </div>
    </DashboardLayout>
  );
};